import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  TableRow,
  TableCell,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import {
  Add,
  Edit,
  DeleteOutline,
  ScienceOutlined,
  FolderOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { CorporateTitle } from '../components/ui/CorporateTitle';
import { CorporateButton } from '../components/ui/CorporateButton';
import { CorporateTable, SortDirection } from '../components/ui/CorporateTable';
import { CorporateDialog } from '../components/ui/CorporateDialog';
import { CorporateTextField } from '../components/ui/CorporateTextField';
import { CorporateRichTextEditor } from '../components/ui/CorporateRichTextEditor';
import { ActionButtonGroup } from '../components/ui/ActionButtonGroup';
import { EmptyState } from '../components/ui/EmptyState';
import { useToast } from '../contexts/ToastContext';
import { apiService } from '../services/api';
import { Ensayo, Proyecto } from '../types';
import { useAuth } from '../contexts/AuthContext';

type EnsayoFormState = {
  nombre: string;
  fecha_ensayo: string;
  codigo: string;
  objetivos: string;
  incidencias: string;
  conclusiones: string;
  proyecto_id: string;
  tipo_ensayo: string;
};

type ProyectoFormState = {
  nombre: string;
  descripcion: string;
  codigo_grant: string;
};

type FormErrors = Partial<Record<keyof EnsayoFormState, string>>;
type ProyectoFormErrors = Partial<Record<keyof ProyectoFormState, string>>;

const emptyFormState: EnsayoFormState = {
  nombre: '',
  fecha_ensayo: '',
  codigo: '',
  objetivos: '',
  incidencias: '',
  conclusiones: '',
  proyecto_id: '',
  tipo_ensayo: '',
};

const emptyProyectoFormState: ProyectoFormState = {
  nombre: '',
  descripcion: '',
  codigo_grant: '',
};

export const EnsayosPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('MANAGE_ENSAYOS');

  const [ensayos, setEnsayos] = useState<Ensayo[]>([]);
  const [proyectos, setProyectos] = useState<Proyecto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formState, setFormState] = useState<EnsayoFormState>(emptyFormState);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isProyectoDialogOpen, setIsProyectoDialogOpen] = useState(false);
  const [proyectoFormState, setProyectoFormState] = useState<ProyectoFormState>(emptyProyectoFormState);
  const [proyectoFormErrors, setProyectoFormErrors] = useState<ProyectoFormErrors>({});
  const [selectedEnsayo, setSelectedEnsayo] = useState<Ensayo | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProyectoId, setSelectedProyectoId] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('fecha');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const loadProyectos = useCallback(async () => {
    try {
      const data = await apiService.getProyectos();
      setProyectos(data);
    } catch (err) {
      console.error('Error loading proyectos:', err);
    }
  }, []);

  const loadEnsayos = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getEnsayos(selectedProyectoId || undefined);
      setEnsayos(data);
    } catch (err) {
      setError(t('ensayos.loadError', 'No se pudieron cargar los ensayos'));
      showError(t('ensayos.loadError', 'No se pudieron cargar los ensayos'));
    } finally {
      setIsLoading(false);
    }
  }, [showError, t, selectedProyectoId]);

  useEffect(() => {
    loadProyectos();
  }, [loadProyectos]);

  useEffect(() => {
    loadEnsayos();
  }, [loadEnsayos]);

  // Filtrar y ordenar ensayos
  const filteredAndSortedEnsayos = useMemo(() => {
    let filtered = [...ensayos];

    // Aplicar búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (ensayo) =>
          ensayo.nombre.toLowerCase().includes(query) ||
          ensayo.codigo.toLowerCase().includes(query)
      );
    }

    // El filtro por proyecto ya se aplica en loadEnsayos, pero mantenemos esto por si acaso

    // Aplicar ordenamiento
    filtered.sort((a, b) => {
      if (!sortDirection) return 0;

      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case 'codigo':
          aValue = a.codigo;
          bValue = b.codigo;
          break;
        case 'nombre':
          aValue = a.nombre.toLowerCase();
          bValue = b.nombre.toLowerCase();
          break;
        case 'fecha':
        default:
          aValue = new Date(a.fecha_ensayo).getTime();
          bValue = new Date(b.fecha_ensayo).getTime();
          break;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [ensayos, searchQuery, sortBy, sortDirection]);

  const handleSort = (key: string, direction: SortDirection) => {
    setSortBy(key);
    setSortDirection(direction);
  };

  const handleInputChange = (
    field: keyof EnsayoFormState,
    value: string
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  // Función para generar código correlativo
  const generateNextCode = useCallback((): string => {
    // Buscar el código más alto existente
    const codes = ensayos
      .map((e) => e.codigo)
      .filter((c) => c.match(/^ENS-\d+$/))
      .map((c) => {
        const match = c.match(/^ENS-(\d+)$/);
        return match ? parseInt(match[1], 10) : 0;
      });

    const maxCode = codes.length > 0 ? Math.max(...codes) : 0;
    const nextCode = maxCode + 1;
    return `ENS-${String(nextCode).padStart(2, '0')}`;
  }, [ensayos]);

  const openCreateDialog = () => {
    if (!canManage) return;
    setSelectedEnsayo(null);
    const nextCode = generateNextCode();
    setFormState({
      ...emptyFormState,
      codigo: nextCode,
      proyecto_id: selectedProyectoId || '',
      tipo_ensayo: '',
    });
    setFormErrors({});
    setIsDialogOpen(true);
  };

  const openCreateProyectoDialog = () => {
    if (!canManage) return;
    setProyectoFormState(emptyProyectoFormState);
    setProyectoFormErrors({});
    setIsProyectoDialogOpen(true);
  };

  const openEditDialog = (ensayo: Ensayo) => {
    if (!canManage) return;
    setSelectedEnsayo(ensayo);
    setFormState({
      nombre: ensayo.nombre,
      fecha_ensayo: ensayo.fecha_ensayo,
      codigo: ensayo.codigo,
      objetivos: ensayo.objetivos,
      incidencias: ensayo.incidencias || '',
      conclusiones: ensayo.conclusiones || '',
      proyecto_id: ensayo.proyecto_id || '',
      tipo_ensayo: ensayo.tipo_ensayo || '',
    });
    setFormErrors({});
    setIsDialogOpen(true);
  };

  const closeDialogs = () => {
    setIsDialogOpen(false);
    setIsDeleteDialogOpen(false);
    setIsProyectoDialogOpen(false);
    setSelectedEnsayo(null);
    setFormErrors({});
    setProyectoFormErrors({});
    setIsSubmitting(false);
  };

  const requestDelete = (ensayo: Ensayo) => {
    if (!canManage) return;
    setSelectedEnsayo(ensayo);
    setIsDeleteDialogOpen(true);
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    if (!formState.nombre.trim()) {
      errors.nombre = t('ensayos.errors.nombre', 'El nombre es obligatorio');
    }
    if (!formState.fecha_ensayo) {
      errors.fecha_ensayo = t('ensayos.errors.fecha', 'La fecha es obligatoria');
    }
    if (!formState.codigo.trim()) {
      errors.codigo = t('ensayos.errors.codigo', 'El código es obligatorio');
    }
    // Validar objetivos eliminando tags HTML
    const objetivosText = formState.objetivos.replace(/<[^>]*>/g, '').trim();
    if (!objetivosText) {
      errors.objetivos = t(
        'ensayos.errors.objetivos',
        'Los objetivos son obligatorios'
      );
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => {
    // Para campos de texto enriquecido, eliminar solo espacios en blanco HTML pero mantener el contenido
    const cleanHtml = (html: string) => {
      if (!html) return null;
      const cleaned = html.trim();
      // Si solo contiene tags HTML vacíos, retornar null
      if (cleaned.replace(/<[^>]*>/g, '').trim() === '') return null;
      return cleaned;
    };

    return {
      nombre: formState.nombre.trim(),
      fecha_ensayo: formState.fecha_ensayo,
      codigo: formState.codigo.trim(),
      objetivos: cleanHtml(formState.objetivos) || '',
      incidencias: cleanHtml(formState.incidencias),
      conclusiones: cleanHtml(formState.conclusiones),
      proyecto_id: formState.proyecto_id || null,
      tipo_ensayo: formState.tipo_ensayo || null,
    };
  };

  const handleProyectoInputChange = (
    field: keyof ProyectoFormState,
    value: string
  ) => {
    setProyectoFormState((prev) => ({ ...prev, [field]: value }));
    setProyectoFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateProyectoForm = (): boolean => {
    const errors: ProyectoFormErrors = {};
    if (!proyectoFormState.nombre.trim()) {
      errors.nombre = 'El nombre es obligatorio';
    }
    setProyectoFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProyectoSubmit = async () => {
    if (!canManage || !validateProyectoForm()) return;
    setIsSubmitting(true);
    try {
      await apiService.createProyecto({
        nombre: proyectoFormState.nombre.trim(),
        descripcion: proyectoFormState.descripcion.trim() || null,
        codigo_grant: proyectoFormState.codigo_grant.trim() || null,
      });
      showSuccess('Proyecto creado correctamente');
      await loadProyectos();
      closeDialogs();
    } catch (err: any) {
      let errorMessage = 'No se pudo crear el proyecto';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      showError(errorMessage);
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!canManage || !validateForm()) return;
    setIsSubmitting(true);
    try {
      const payload = buildPayload();
      if (selectedEnsayo) {
        await apiService.updateEnsayo(selectedEnsayo.id, payload);
        showSuccess(
          t('ensayos.updateSuccess', 'Ensayo actualizado correctamente')
        );
      } else {
        await apiService.createEnsayo(payload);
        showSuccess(t('ensayos.createSuccess', 'Ensayo creado correctamente'));
      }
      await loadEnsayos();
      closeDialogs();
    } catch (err: any) {
      // Extraer mensaje de error del backend
      let errorMessage = t('ensayos.saveError', 'No se pudo guardar el ensayo');
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // Manejar errores de validación de Pydantic (422)
        if (err.response.status === 422 && errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Pydantic devuelve un array de errores
            const validationErrors = errorData.detail.map((e: any) => {
              const field = e.loc && e.loc.length > 1 ? e.loc[e.loc.length - 1] : 'campo';
              const msg = e.msg || 'Error de validación';
              return `${field}: ${msg}`;
            });
            errorMessage = validationErrors.join('; ');
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          }
        } else if (errorData.detail) {
          // Otros errores con detail
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      showError(errorMessage);
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!canManage || !selectedEnsayo) return;
    setIsSubmitting(true);
    try {
      await apiService.deleteEnsayo(selectedEnsayo.id);
      showSuccess(t('ensayos.deleteSuccess', 'Ensayo eliminado correctamente'));
      await loadEnsayos();
      closeDialogs();
    } catch (err) {
      showError(t('ensayos.deleteError', 'No se pudo eliminar el ensayo'));
      setIsSubmitting(false);
    }
  };

  const formatDate = (isoDate: string) => {
    if (!isoDate) return '—';
    const parsed = new Date(isoDate);
    return parsed.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Layout>
      <Box>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          gap={2}
          mb={4}
        >
          <Box>
            <CorporateTitle level="h3">
              {t('ensayos.title', 'Ensayos de laboratorio')}
            </CorporateTitle>
            <Typography color="text.secondary" variant="body2">
              {t(
                'ensayos.subtitle',
                'Gestiona los ensayos mecánicos realizados sobre vigas de madera.'
              )}
            </Typography>
          </Box>
          {canManage && (
            <Box display="flex" gap={2}>
              <CorporateButton
                startIcon={<FolderOutlined />}
                onClick={openCreateProyectoDialog}
                variant="outlined"
              >
                Crear Proyecto
              </CorporateButton>
              <CorporateButton
                startIcon={<Add />}
                onClick={openCreateDialog}
              >
                {t('ensayos.add', 'Nuevo ensayo')}
              </CorporateButton>
            </Box>
          )}
        </Box>

        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="40vh"
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        ) : (
          <>
            <Box mb={2} display="flex" gap={2} flexWrap="wrap">
              <TextField
                placeholder={t('ensayos.searchPlaceholder', 'Buscar por nombre o código...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  maxWidth: 400,
                  flex: 1,
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.paper',
                  },
                }}
              />
              <FormControl 
                sx={{ 
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.paper',
                  },
                }}
              >
                <InputLabel>Filtrar por proyecto</InputLabel>
                <Select
                  value={selectedProyectoId}
                  onChange={(e) => setSelectedProyectoId(e.target.value)}
                  label="Filtrar por proyecto"
                >
                  <MenuItem value="">Todos los proyectos</MenuItem>
                  {proyectos.map((proyecto) => (
                    <MenuItem key={proyecto.id} value={proyecto.id}>
                      {proyecto.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {filteredAndSortedEnsayos.length === 0 ? (
              searchQuery.trim() ? (
                <EmptyState
                  icon={<Search />}
                  title={t('ensayos.noSearchResults', 'No se encontraron resultados')}
                  description={t(
                    'ensayos.noSearchResultsDescription',
                    'No hay ensayos que coincidan con "{{query}}". Intenta con otros términos de búsqueda.',
                    { query: searchQuery }
                  )}
                />
              ) : ensayos.length === 0 ? (
                <EmptyState
                  icon={<ScienceOutlined />}
                  title={t('ensayos.emptyTitle', 'Sin ensayos registrados')}
                  description={t(
                    'ensayos.emptyDescription',
                    'Registra los ensayos de laboratorio para hacer seguimiento del comportamiento estructural.'
                  )}
                  actionLabel={canManage ? t('ensayos.add', 'Nuevo ensayo') : undefined}
                  onAction={canManage ? openCreateDialog : undefined}
                />
              ) : null
            ) : (
              <CorporateTable
              headers={[
                { label: t('ensayos.table.codigo', 'Código'), key: 'codigo', sortable: true },
                { label: 'Proyecto', key: 'proyecto', sortable: false },
                { label: t('ensayos.table.nombre', 'Nombre del ensayo'), key: 'nombre', sortable: true },
                { label: t('ensayos.table.fecha', 'Fecha'), key: 'fecha', sortable: true },
                { label: t('ensayos.table.objetivos', 'Objetivos del ensayo'), key: 'objetivos', width: '18%' },
                { label: t('ensayos.table.incidencias', 'Incidencias'), key: 'incidencias', width: '18%' },
                { label: t('ensayos.table.conclusiones', 'Conclusiones'), key: 'conclusiones', width: '18%' },
                ...(canManage ? [t('ensayos.table.acciones', 'Acciones')] : []),
              ]}
              onSort={handleSort}
              sortBy={sortBy}
              sortDirection={sortDirection}
            >
              {filteredAndSortedEnsayos.map((ensayo) => (
              <TableRow 
                key={ensayo.id}
                onClick={() => navigate(`/ensayos/${ensayo.id}`)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <TableCell>
                  <Chip
                    size="small"
                    label={ensayo.codigo}
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell>
                  {ensayo.proyecto ? (
                    <Chip
                      size="small"
                      label={ensayo.proyecto.nombre}
                      color="secondary"
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {ensayo.nombre}
                  </Typography>
                </TableCell>
                <TableCell width="120px">
                  <Typography variant="body2" fontWeight={600}>
                    {formatDate(ensayo.fecha_ensayo)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ width: '18%' }}>
                  <Tooltip
                    title={
                      <Box
                        sx={{
                          maxWidth: 400,
                          '& p': { margin: 0 },
                        }}
                        dangerouslySetInnerHTML={{ __html: ensayo.objetivos }}
                      />
                    }
                    arrow
                    placement="top"
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        cursor: 'help',
                        '& p': {
                          margin: 0,
                        },
                      }}
                      dangerouslySetInnerHTML={{ __html: ensayo.objetivos }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '18%' }}>
                  {ensayo.incidencias ? (
                    <Tooltip
                      title={
                        <Box
                          sx={{
                            maxWidth: 400,
                            '& p': { margin: 0 },
                          }}
                          dangerouslySetInnerHTML={{ __html: ensayo.incidencias }}
                        />
                      }
                      arrow
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          cursor: 'help',
                          '& p': {
                            margin: 0,
                          },
                        }}
                        dangerouslySetInnerHTML={{ __html: ensayo.incidencias }}
                      />
                    </Tooltip>
                  ) : (
                    <Chip
                      label={t('ensayos.noIncidencias', 'Sin incidencias')}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  )}
                </TableCell>
                <TableCell sx={{ width: '18%' }}>
                  {ensayo.conclusiones ? (
                    <Tooltip
                      title={
                        <Box
                          sx={{
                            maxWidth: 400,
                            '& p': { margin: 0 },
                          }}
                          dangerouslySetInnerHTML={{ __html: ensayo.conclusiones }}
                        />
                      }
                      arrow
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          cursor: 'help',
                          '& p': {
                            margin: 0,
                          },
                        }}
                        dangerouslySetInnerHTML={{ __html: ensayo.conclusiones }}
                      />
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </TableCell>
                {canManage && (
                  <TableCell 
                    width="120px"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ cursor: 'default' }}
                  >
                    <ActionButtonGroup
                      actions={[
                        {
                          icon: <Edit fontSize="small" />,
                          tooltip: t('common.edit', 'Editar'),
                          onClick: () => openEditDialog(ensayo),
                        },
                        {
                          icon: <DeleteOutline fontSize="small" />,
                          tooltip: t('common.delete', 'Eliminar'),
                          onClick: () => requestDelete(ensayo),
                          destructive: true,
                        },
                      ]}
                    />
                  </TableCell>
                )}
              </TableRow>
              ))}
              </CorporateTable>
            )}
          </>
        )}
      </Box>

      {/* Crear / editar */}
      <CorporateDialog
        open={isDialogOpen}
        onClose={closeDialogs}
        title={
          selectedEnsayo
            ? t('ensayos.edit', 'Editar ensayo')
            : t('ensayos.add', 'Nuevo ensayo')
        }
        icon={<ScienceOutlined />}
        maxWidth="lg"
        fullWidth
        actions={
          <>
            <CorporateButton onClick={closeDialogs} color="inherit">
              {t('common.cancel', 'Cancelar')}
            </CorporateButton>
            <CorporateButton
              onClick={handleSubmit}
              disabled={isSubmitting}
              colorVariant="accent"
            >
              {selectedEnsayo
                ? t('common.save', 'Guardar')
                : t('common.save', 'Guardar')}
            </CorporateButton>
          </>
        }
      >
        <Stack spacing={3}>
          <CorporateTextField
            label={t('ensayos.fields.codigo', 'Código del ensayo')}
            value={formState.codigo}
            onChange={(event) => handleInputChange('codigo', event.target.value)}
            error={Boolean(formErrors.codigo)}
            helperText={formErrors.codigo}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Proyecto</InputLabel>
            <Select
              value={formState.proyecto_id}
              onChange={(e) => handleInputChange('proyecto_id', e.target.value)}
              label="Proyecto"
            >
              <MenuItem value="">Sin proyecto</MenuItem>
              {proyectos.map((proyecto) => (
                <MenuItem key={proyecto.id} value={proyecto.id}>
                  {proyecto.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Tipo de Ensayo</InputLabel>
            <Select
              value={formState.tipo_ensayo}
              onChange={(e) => handleInputChange('tipo_ensayo', e.target.value)}
              label="Tipo de Ensayo"
            >
              <MenuItem value="">Sin especificar</MenuItem>
              <MenuItem value="3 Puntos">3 Puntos</MenuItem>
              <MenuItem value="4 Puntos">4 Puntos</MenuItem>
            </Select>
          </FormControl>
          <CorporateTextField
            label={t('ensayos.fields.nombre', 'Nombre del ensayo')}
            value={formState.nombre}
            onChange={(event) => handleInputChange('nombre', event.target.value)}
            error={Boolean(formErrors.nombre)}
            helperText={formErrors.nombre}
            fullWidth
          />
          <CorporateTextField
            type="date"
            label={t('ensayos.fields.fecha', 'Fecha del ensayo')}
            InputLabelProps={{ shrink: true }}
            value={formState.fecha_ensayo}
            onChange={(event) =>
              handleInputChange('fecha_ensayo', event.target.value)
            }
            error={Boolean(formErrors.fecha_ensayo)}
            helperText={formErrors.fecha_ensayo}
            fullWidth
          />
          <CorporateRichTextEditor
            label={t('ensayos.fields.objetivos', 'Objetivos del ensayo')}
            value={formState.objetivos}
            onChange={(value) => handleInputChange('objetivos', value)}
            error={Boolean(formErrors.objetivos)}
            helperText={formErrors.objetivos}
            minHeight={200}
          />
          <CorporateRichTextEditor
            label={t('ensayos.fields.incidencias', 'Incidencias')}
            value={formState.incidencias}
            onChange={(value) => handleInputChange('incidencias', value)}
            minHeight={150}
          />
          <CorporateRichTextEditor
            label={t('ensayos.fields.conclusiones', 'Conclusiones')}
            value={formState.conclusiones}
            onChange={(value) => handleInputChange('conclusiones', value)}
            minHeight={150}
          />
        </Stack>
      </CorporateDialog>

      {/* Confirmación de borrado */}
      <CorporateDialog
        open={isDeleteDialogOpen}
        onClose={closeDialogs}
        title={t('ensayos.delete', 'Eliminar ensayo')}
        icon={<DeleteOutline />}
        actions={
          <>
            <CorporateButton onClick={closeDialogs} color="inherit">
              {t('common.cancel', 'Cancelar')}
            </CorporateButton>
            <CorporateButton
              color="error"
              onClick={handleDelete}
              disabled={isSubmitting}
            >
              {t('common.delete', 'Eliminar')}
            </CorporateButton>
          </>
        }
      >
        <Typography variant="body2">
          {t(
            'ensayos.deleteWarning',
            '¿Estás seguro de que deseas eliminar el ensayo "{{nombre}}"?',
            { nombre: selectedEnsayo?.nombre || '' }
          )}
        </Typography>
      </CorporateDialog>

      {/* Modal de crear proyecto */}
      <CorporateDialog
        open={isProyectoDialogOpen}
        onClose={closeDialogs}
        title="Crear Proyecto"
        icon={<FolderOutlined />}
        maxWidth="md"
        fullWidth
        actions={
          <>
            <CorporateButton onClick={closeDialogs} color="inherit">
              {t('common.cancel', 'Cancelar')}
            </CorporateButton>
            <CorporateButton
              onClick={handleProyectoSubmit}
              disabled={isSubmitting}
              colorVariant="accent"
            >
              {t('common.save', 'Guardar')}
            </CorporateButton>
          </>
        }
      >
        <Stack spacing={3}>
          <CorporateTextField
            label="Nombre del proyecto"
            value={proyectoFormState.nombre}
            onChange={(event) => handleProyectoInputChange('nombre', event.target.value)}
            error={Boolean(proyectoFormErrors.nombre)}
            helperText={proyectoFormErrors.nombre}
            fullWidth
            required
          />
          <CorporateTextField
            label="Descripción"
            value={proyectoFormState.descripcion}
            onChange={(event) => handleProyectoInputChange('descripcion', event.target.value)}
            multiline
            rows={4}
            fullWidth
          />
          <CorporateTextField
            label="Código de Grant"
            value={proyectoFormState.codigo_grant}
            onChange={(event) => handleProyectoInputChange('codigo_grant', event.target.value)}
            fullWidth
            helperText="Código del grant asociado (opcional)"
          />
        </Stack>
      </CorporateDialog>
    </Layout>
  );
};


